import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { Eye, Check, X } from 'lucide-react';
import Layout from '../../components/layout/Layout';
import MobileCardList, { TableColumn } from '../../components/common/MobileCardList';
import { formatDateTime } from '../../utils/dateTime';
import api from '../../services/api';

interface DispatchItem {
  id: number;
  productDescription: string;
  productCategoryId: number;
  batchNo: string;
  skuCode?: string;
  customerSapCode?: string;
  mfgDate?: string;
  expiryDate?: string;
  goodQty: number;
  cdamQty: number;
  damQty: number;
  palletCount: number;
  storageSelections: Array<{
    inventoryStorageId: number;
    palletsToDispatch: number;
  }>;
}

interface Dispatch {
  id: number;
  dispatchNumber: string;
  clientName: string;
  warehouseName: string;
  status: string;
  createdAt: string;
  lrNumber?: string | null;
  eWaybillNumber?: string | null;
  destination?: string | null;
  remarks?: string | null;
  items?: DispatchItem[];
}

interface DispatchListResponse {
  items: Dispatch[];
  meta: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

const DispatchApprovals: React.FC = () => {
  const [selectedDispatch, setSelectedDispatch] = useState<Dispatch | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmAction, setConfirmAction] = useState<'approve' | 'reject' | null>(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const queryClient = useQueryClient();

  // Fetch submitted dispatches
  const { data: dispatchesData, isLoading } = useQuery<DispatchListResponse>({
    queryKey: ['dispatches', 'submitted'],
    queryFn: async () => {
      const response = await api.get<DispatchListResponse>('/dispatch?status=submitted');
      return response.data;
    },
  });



  // Fetch dispatch items
  const { data: dispatchItems } = useQuery<DispatchItem[]>({
    queryKey: ['dispatch', selectedDispatch?.id, 'items'],
    queryFn: async () => {
      if (!selectedDispatch) return [];
      const response = await api.get<DispatchItem[]>(`/dispatch/${selectedDispatch.id}/items`);
      return response.data;
    },
    enabled: !!selectedDispatch,
  });

  // Approve dispatch mutation
  const approveMutation = useMutation({
    mutationFn: async (dispatchId: number) => {
      return api.post(`/dispatch/${dispatchId}/approve`);
    },
    onSuccess: () => {
      toast.success('Dispatch approved successfully');
      queryClient.invalidateQueries({ queryKey: ['dispatches'] });
      queryClient.invalidateQueries({ queryKey: ['inventory'] });
      queryClient.invalidateQueries({ queryKey: ['inventory-summary'] });
      queryClient.invalidateQueries({ queryKey: ['transactions'] });
      setShowConfirmModal(false);
      setSelectedDispatch(null);
      setShowDetailsModal(false);
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to approve dispatch');
    },
  });

  // Reject dispatch mutation
  const rejectMutation = useMutation({
    mutationFn: async ({ dispatchId, rejectionReason }: { dispatchId: number; rejectionReason: string }) => {
      return api.post(`/dispatch/${dispatchId}/reject`, { rejectionReason });
    },
    onSuccess: () => {
      toast.success('Dispatch rejected successfully');
      queryClient.invalidateQueries({ queryKey: ['dispatches'] });
      setShowConfirmModal(false);
      setSelectedDispatch(null);
      setShowDetailsModal(false);
      setRejectionReason('');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to reject dispatch');
    },
  });

  const handleAction = (action: 'approve' | 'reject', dispatch: Dispatch) => {
    setSelectedDispatch(dispatch);
    setConfirmAction(action);
    setRejectionReason('');
    setShowConfirmModal(true);
  };

  const handleConfirm = () => {
    if (!selectedDispatch || !confirmAction) return;

    if (confirmAction === 'approve') {
      approveMutation.mutate(selectedDispatch.id);
    } else {
      if (!rejectionReason.trim()) {
        toast.error('Please provide a rejection reason');
        return;
      }
      rejectMutation.mutate({ dispatchId: selectedDispatch.id, rejectionReason });
    }
  };

  const handleViewDetails = (dispatch: Dispatch) => {
    setSelectedDispatch(dispatch);
    setShowDetailsModal(true);
  };

  // Shared by the desktop table and the mobile card list
  const dispatchApprovalColumns: TableColumn[] = [
    { key: 'dispatchNumber', label: 'Dispatch ID' },
    { key: 'clientName', label: 'Client Name' },
    { key: 'warehouseName', label: 'Warehouse' },
    { key: 'createdAt', label: 'Created At', render: (value) => formatDateTime(value) },
    {
      key: 'actions',
      label: 'Actions',
      render: (_, dispatch) => (
        <div className="flex items-center justify-end gap-3 flex-wrap">
          <button
            onClick={() => handleViewDetails(dispatch)}
            className="text-brand-600 hover:text-brand-900 inline-flex items-center"
          >
            <Eye className="h-4 w-4 mr-1" />
            View
          </button>
          <button
            onClick={() => handleAction('approve', dispatch)}
            disabled={approveMutation.isPending || rejectMutation.isPending}
            className="text-green-600 hover:text-green-900 inline-flex items-center disabled:opacity-50"
          >
            <Check className="h-4 w-4 mr-1" />
            Approve
          </button>
          <button
            onClick={() => handleAction('reject', dispatch)}
            disabled={approveMutation.isPending || rejectMutation.isPending}
            className="text-red-600 hover:text-red-900 inline-flex items-center disabled:opacity-50"
          >
            <X className="h-4 w-4 mr-1" />
            Reject
          </button>
        </div>
      ),
    },
  ];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600"></div>
      </div>
    );
  }

  return (
    <Layout>
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-900">Dispatch Approvals</h1>
        <div className="text-sm text-gray-500">
          {dispatchesData?.meta.total || 0} pending approvals
        </div>
      </div>

      <MobileCardList
        columns={dispatchApprovalColumns}
        data={dispatchesData?.items || []}
        emptyMessage="No dispatches pending approval"
      />

      <div className="hidden lg:block bg-white shadow rounded-lg overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gradient-to-r from-gray-100 to-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wide">
                Dispatch ID
              </th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wide">
                Client Name
              </th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wide">
                Warehouse
              </th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wide">
                Created At
              </th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wide">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {dispatchesData?.items.map((dispatch) => (
              <tr key={dispatch.id} className="hover:bg-gradient-to-r hover:from-brand-50 hover:to-transparent hover:shadow-md hover:scale-[1.01] transform transition-all duration-300 border-b border-gray-200 last:border-b-0 group">
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 group-hover:text-primary-600 transition-colors duration-200">
                  {dispatch.dispatchNumber}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {dispatch.clientName}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {dispatch.warehouseName}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {formatDateTime(dispatch.createdAt)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                  <button
                    onClick={() => handleViewDetails(dispatch)}
                    className="text-brand-600 hover:text-brand-900 inline-flex items-center"
                  >
                    <Eye className="h-4 w-4 mr-1" />
                    View
                  </button>
                  <button
                    onClick={() => handleAction('approve', dispatch)}
                    disabled={approveMutation.isPending || rejectMutation.isPending}
                    className="text-green-600 hover:text-green-900 inline-flex items-center disabled:opacity-50"
                  >
                    <Check className="h-4 w-4 mr-1" />
                    Approve
                  </button>
                  <button
                    onClick={() => handleAction('reject', dispatch)}
                    disabled={approveMutation.isPending || rejectMutation.isPending}
                    className="text-red-600 hover:text-red-900 inline-flex items-center disabled:opacity-50"
                  >
                    <X className="h-4 w-4 mr-1" />
                    Reject
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {dispatchesData?.items.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-500">No dispatches pending approval</p>
          </div>
        )}
      </div>

      {/* Details Modal */}
      {showDetailsModal && selectedDispatch && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-end justify-center p-3 lg:p-0 lg:block">
          <div className="relative lg:top-20 mx-auto p-5 border w-full lg:w-11/12 max-w-4xl shadow-lg rounded-2xl lg:rounded-md bg-white max-h-[85vh] lg:max-h-none overflow-y-auto animate-slide-up lg:animate-none">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                Dispatch Details - {selectedDispatch.dispatchNumber}
              </h3>
              <button
                onClick={() => setShowDetailsModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Client</label>
                  <p className="mt-1 text-sm text-gray-900">{selectedDispatch.clientName}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Warehouse</label>
                  <p className="mt-1 text-sm text-gray-900">{selectedDispatch.warehouseName}</p>
                </div>
                {selectedDispatch.lrNumber && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700">LR Number</label>
                    <p className="mt-1 text-sm font-semibold text-gray-900 font-mono">{selectedDispatch.lrNumber}</p>
                  </div>
                )}
                {selectedDispatch.eWaybillNumber && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700">E-Waybill Number</label>
                    <p className="mt-1 text-sm font-semibold text-gray-900 font-mono">{selectedDispatch.eWaybillNumber}</p>
                  </div>
                )}
                {selectedDispatch.destination && (
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-700">Destination</label>
                    <p className="mt-1 text-sm text-gray-900 break-words whitespace-pre-wrap">{selectedDispatch.destination}</p>
                  </div>
                )}
                {selectedDispatch.remarks && (
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-700">Dispatch Remarks</label>
                    <p className="mt-1 text-sm text-gray-900 break-words whitespace-pre-wrap">{selectedDispatch.remarks}</p>
                  </div>
                )}
              </div>

              <div>
                <h4 className="text-md font-medium text-gray-900 mb-2">Items</h4>
                <MobileCardList
                  columns={[
                    { key: 'productDescription', label: 'Product' },
                    { key: 'skuCode', label: 'SKU Code', render: (value) => <span className="font-semibold">{value || '-'}</span> },
                    { key: 'customerSapCode', label: 'SAP Code', render: (value) => value || '-' },
                    { key: 'batchNo', label: 'Batch No' },
                    { key: 'mfgDate', label: 'MFG Date', render: (value) => (value ? new Date(value).toLocaleDateString() : '-') },
                    { key: 'expiryDate', label: 'Expiry Date', render: (value) => (value ? new Date(value).toLocaleDateString() : '-') },
                    { key: 'goodQty', label: 'Good Qty' },
                    { key: 'cdamQty', label: 'CDAM Qty' },
                    { key: 'damQty', label: 'DAM Qty' },
                    { key: 'palletCount', label: 'Pallets' },
                    { key: 'storageSelections', label: 'Storage', render: (value) => `${value?.length || 0} locations` },
                  ]}
                  data={dispatchItems || []}
                />
                <div className="hidden lg:block overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gradient-to-r from-gray-100 to-gray-50">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-semibold text-gray-700 uppercase">
                          Product
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-semibold text-gray-700 uppercase">
                          SKU Code
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-semibold text-gray-700 uppercase">
                          SAP Code
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-semibold text-gray-700 uppercase">
                          Batch No
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-semibold text-gray-700 uppercase">
                          MFG Date
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-semibold text-gray-700 uppercase">
                          Expiry Date
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-semibold text-gray-700 uppercase">
                          Good Qty
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-semibold text-gray-700 uppercase">
                          CDAM Qty
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-semibold text-gray-700 uppercase">
                          DAM Qty
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-semibold text-gray-700 uppercase">
                          Pallets
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-semibold text-gray-700 uppercase">
                          Storage
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {dispatchItems?.map((item) => (
                        <tr key={item.id} className="hover:bg-gradient-to-r hover:from-brand-50 hover:to-transparent hover:shadow-md hover:scale-[1.01] transform transition-all duration-300 border-b border-gray-200 last:border-b-0 group">
                          <td className="px-4 py-2 text-sm text-gray-900">
                            {item.productDescription}
                          </td>
                          <td className="px-4 py-2 text-sm font-semibold text-gray-900">
                            {item.skuCode || '-'}
                          </td>
                          <td className="px-4 py-2 text-sm text-gray-600">
                            {item.customerSapCode || '-'}
                          </td>
                          <td className="px-4 py-2 text-sm text-gray-900">
                            {item.batchNo}
                          </td>
                          <td className="px-4 py-2 text-sm text-gray-600">
                            {item.mfgDate ? new Date(item.mfgDate).toLocaleDateString() : '-'}
                          </td>
                          <td className="px-4 py-2 text-sm text-gray-600">
                            {item.expiryDate ? new Date(item.expiryDate).toLocaleDateString() : '-'}
                          </td>
                          <td className="px-4 py-2 text-sm text-gray-900">
                            {item.goodQty}
                          </td>
                          <td className="px-4 py-2 text-sm text-gray-900">
                            {item.cdamQty}
                          </td>
                          <td className="px-4 py-2 text-sm text-gray-900">
                            {item.damQty}
                          </td>
                          <td className="px-4 py-2 text-sm text-gray-900">
                            {item.palletCount}
                          </td>
                          <td className="px-4 py-2 text-sm text-gray-900">
                            {item.storageSelections?.length || 0} locations
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <div className="flex flex-col-reverse gap-3 lg:flex-row lg:justify-end lg:gap-0 lg:space-x-3 mt-6">
              <button
                onClick={() => setShowDetailsModal(false)}
                className="w-full lg:w-auto min-h-[44px] lg:min-h-0 px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Close
              </button>
              <button
                onClick={() => handleAction('approve', selectedDispatch)}
                disabled={approveMutation.isPending || rejectMutation.isPending}
                className="w-full lg:w-auto min-h-[44px] lg:min-h-0 px-4 py-2 bg-green-600 text-white rounded-md text-sm font-medium hover:bg-green-700 disabled:opacity-50"
              >
                Approve
              </button>
              <button
                onClick={() => handleAction('reject', selectedDispatch)}
                disabled={approveMutation.isPending || rejectMutation.isPending}
                className="w-full lg:w-auto min-h-[44px] lg:min-h-0 px-4 py-2 bg-red-600 text-white rounded-md text-sm font-medium hover:bg-red-700 disabled:opacity-50"
              >
                Reject
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      {showConfirmModal && selectedDispatch && confirmAction && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-end justify-center p-3 lg:p-0 lg:block">
          <div className="relative lg:top-20 mx-auto p-5 border w-full lg:w-96 shadow-lg rounded-2xl lg:rounded-md bg-white max-h-[85vh] overflow-y-auto animate-slide-up lg:animate-none">
            <div className="mt-3 text-center">
              <h3 className="text-lg font-medium text-gray-900">
                {confirmAction === 'approve' ? 'Approve Dispatch' : 'Reject Dispatch'}
              </h3>
              <div className="mt-2 px-7 py-3">
                <p className="text-sm text-gray-500">
                  Are you sure you want to {confirmAction} dispatch {selectedDispatch.dispatchNumber}?
                  {confirmAction === 'approve' && (
                    <span className="block mt-2 text-brand-600">
                      This will reduce inventory and cannot be undone.
                    </span>
                  )}
                </p>
                {confirmAction === 'reject' && (
                  <div className="mt-4 text-left">
                    <label htmlFor="rejectionReason" className="block text-sm font-medium text-gray-700 mb-2">
                      Rejection Reason <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      id="rejectionReason"
                      value={rejectionReason}
                      onChange={(e) => setRejectionReason(e.target.value)}
                      rows={3}
                      maxLength={1000}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                      placeholder="Please provide a reason for rejection..."
                    />
                  </div>
                )}
              </div>
              <div className="flex justify-center space-x-3 mt-4">
                <button
                  onClick={() => {
                    setShowConfirmModal(false);
                    setRejectionReason('');
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirm}
                  disabled={approveMutation.isPending || rejectMutation.isPending}
                  className={`px-4 py-2 rounded-md text-sm font-medium text-white disabled:opacity-50 ${
                    confirmAction === 'approve'
                      ? 'bg-green-600 hover:bg-green-700'
                      : 'bg-red-600 hover:bg-red-700'
                  }`}
                >
                  {approveMutation.isPending || rejectMutation.isPending
                    ? 'Processing...'
                    : confirmAction === 'approve'
                    ? 'Approve'
                    : 'Reject'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
    </Layout>
  );
};

export default DispatchApprovals;
